import Carousel from '@atoms/Carousel/Carousel';
import ListingFilterItem from '@atoms/ListingFilterItem/ListingFilterItem';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IListingFilterGroup } from '@models/IListing';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly filters: IListingFilterGroup[];
  readonly isDrawerFilter?: boolean;
  readonly onClickCallback: (
    selectedListingFilters: IListingFilterGroup[]
  ) => void;
  readonly selectedListingFilters?: string[];
  readonly type?: ListingTypeEnum;
  readonly site?: SiteEnum;
}

const ListingFilters = ({
  filters,
  type = ListingTypeEnum.Colour,
  isDrawerFilter,
  onClickCallback,
  selectedListingFilters = [],
  site,
}: Props) => {
  const [isReady, setIsReady] = useState(false);

  const handleClick = ({ filter }: IListingFilterGroup) => {
    const id = filter.id.toString();
    const newSelectedListingFilters = selectedListingFilters.includes(id)
      ? selectedListingFilters.filter((selectedId) => selectedId !== id)
      : [...selectedListingFilters, id];

    const selectedFilterObjects = newSelectedListingFilters
      .slice() // Create a copy of the array to avoid mutating the original
      .reverse() // Reverse the array to ensure the last item is processed first
      .map((selectedId) => {
        const selectedFilter = filters.find(
          ({ filter }) => filter.id.toString() === selectedId
        );
        return selectedFilter
          ? {
              filter: {
                id: selectedFilter.filter.id.toString(),
                label: selectedFilter.filter.label,
              },
              featuredInspiration: selectedFilter.featuredInspiration,
              inspirationCard: selectedFilter.inspirationCard,
            }
          : null;
      })
      .filter((filter) => filter !== null) as IListingFilterGroup[];

    window.dataLayer.push({
      event: 'trackingEvent_navigation_circle',
      event_name_variable: 'navigation_circle',
      trackingEvent_navigation_circle: {
        navigation_circle: filter.label,
        navigation_category: type,
      },
    });

    onClickCallback(selectedFilterObjects);
  };

  const breakpoints = useMemo(
    () => ({
      0: {
        slidesPerGroup: 3,
        spaceBetween: type === ListingTypeEnum.Product ? 16 : 8,
        enabled: filters.length > 3,
      },
      752: {
        enabled: filters.length > 8,
        slidesPerGroup: 1,
        spaceBetween: type === ListingTypeEnum.Product ? 32 : 40,
      },
    }),
    [type, filters.length]
  );

  return (
    <section className="tw-w-full tw-bg-transparent tw-text-black">
      <Carousel
        onSwiper={() => setIsReady(true)} // Added to prevent layout shift
        className={
          !isReady
            ? 'tw-opacity-0'
            : 'tw-opacity-100 tw-transition-opacity tw-duration-500'
        }
        id="listing-filters-carousel"
        breakpointsBase="window"
        enabled={true}
        navId="listing-filters"
        centerInsufficientSlides
        pagination={{
          el: `#listing-filters-pagination`,
          clickable: false,
          bulletClass: `tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-full tw-bg-grey-20 ${
            site === SiteEnum.Deco && 'deco'
          }`,
        }}
        slidesPerView="auto"
        additionalModules={[Pagination]}
        speed={1200}
        breakpoints={breakpoints}
      >
        {filters.map(({ colourHexCode, image, filter }, index) => (
          <SwiperSlide
            key={`${filter.id}-${index}`}
            className={classNames({
              '!tw-w-[90px] md:!tw-w-[120px]':
                site === SiteEnum.B2B && image?.url,
              '!tw-w-[120px]': site === SiteEnum.Deco && image?.url,
              '!tw-w-[90px]': !image?.url,
            })}
          >
            <button
              key={filter.id}
              onClick={() => handleClick({ filter })}
              className="tw-group tw-outline-none"
            >
              <ListingFilterItem
                isDrawerFilter={isDrawerFilter}
                colourHexCode={colourHexCode}
                image={image}
                filter={filter}
                selected={selectedListingFilters.includes(filter.id.toString())}
                site={site}
                isLarge={site === SiteEnum.B2B}
              />
            </button>
          </SwiperSlide>
        ))}
      </Carousel>
      <div
        id="listing-filters-pagination"
        className={classNames(
          'tw-flex tw-w-full tw-flex-wrap tw-justify-center tw-gap-xs tw-pt-sm md:tw-hidden'
        )}
      />
    </section>
  );
};

export default ListingFilters;
